import { Document, SignableDocument } from 'app/models/document-params'

// Placeholder until wired to a real source (LOS-5519 follow-up).
export const DEFAULT_OPERATIONS_EMAIL = 'LendOS-Operations@blackstone.com'
// Company named in trade email subjects. Fixed to the tenant rather than read
// from REACT_APP_COMPANY, which is the deployment slug (e.g. 'rightpedal').
export const DEFAULT_COMPANY_NAME = 'Blackstone'

export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunkSize)),
    )
  }
  return btoa(binary)
}

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC']

export const formatEmailDate = (date: string | null | undefined): string => {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${day}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`
}

export const derivePdfContents = (
  attachments: Document[],
  signableDocs: SignableDocument[],
  subTradeId: string | undefined,
): string => {
  if (!subTradeId) return ''
  const atts = attachments.filter(d => d.contractId?.id === subTradeId)
  const sds = signableDocs.filter(d => d.contractId === subTradeId)
  const has = (tag: string, docType: string): boolean =>
    atts.some(d => d.tags.includes(tag)) ||
    sds.some(d => d.documentType === docType)
  // AL (Affiliate Letter) is intentionally excluded: it is not part of the
  // trade packet PDF, so it must not be advertised in "Trade PDF Contents".
  const parts: string[] = []
  if (has('C', 'ConfirmationDocument')) parts.push('C')
  if (has('AA', 'AssignmentAgreementDocument')) parts.push('AA')
  if (has('FM', 'FundingMemoDocument')) parts.push('FM')
  return parts.join(', ')
}

// LOS-5260 PDF filename embeds the operator-facing sub-trade id (ST-NNNNNN-NNN).
export const extractSubtradeIdFromFileName = (fileName: string): string => {
  const m = fileName.match(/(ST-\d+-\d+)/)
  return m ? m[1] : ''
}

// Per LOS-5519 spec: non-alphanumeric runs → '-', trim, cap at 25 chars on a
// word boundary when possible (back off to the last '-' before the limit).
export const slugifyShortName = (name: string): string => {
  const slug = (name || '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (slug.length <= 25) return slug
  const capped = slug.slice(0, 25)
  const lastDash = capped.lastIndexOf('-')
  return lastDash > 0 ? capped.slice(0, lastDash) : capped
}

const utf8ToBase64 = (s: string): string =>
  btoa(unescape(encodeURIComponent(s)))

// Entity/deal names are user-editable portal data: escape them before they
// enter the email HTML so a name containing markup renders as text.
export const escapeHtml = (s: string): string =>
  (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

// MIME headers are line-delimited: strip CR/LF from interpolated header
// values so a crafted name can't inject additional headers.
export const sanitizeHeaderValue = (s: string): string =>
  (s ?? '').replace(/[\r\n]+/g, ' ')

// RFC 2046 restricts boundary characters, and the boundary sits inside a
// quoted Content-Type parameter: reduce interpolated ids to [A-Za-z0-9_] so
// an unexpected character can't terminate the parameter or break the MIME
// structure.
export const sanitizeBoundaryId = (s: string): string =>
  (s ?? '').replace(/[^A-Za-z0-9]/g, '_')

export type TradeEmlParams = {
  subtradeIdLabel: string
  tradeDate: string
  settlementDate: string
  dealName: string
  sellerName: string
  buyerName: string
  paymentMethod: string
  pdfContents: string
  to: string[]
  cc?: string[]
  operationsEmail: string
  pdfFileName: string
  pdfBase64: string
}

export const buildTradeEml = (p: TradeEmlParams): string => {
  const boundary = `----=_LENDOS_${sanitizeBoundaryId(p.subtradeIdLabel) || 'TRADE'}`
  const subject = sanitizeHeaderValue(
    `Seller: ${p.sellerName} – Buyer: ${p.buyerName} – ${p.subtradeIdLabel}` +
      ` – LendOS – ${p.dealName} – SD: ${p.settlementDate}`,
  )

  const html = [
    '<html><body style="font-family:Arial,Helvetica,sans-serif;font-size:13px;">',
    '<p>Hello,</p>',
    '<p>Please note the below trade that settled in the LendOS platform:</p>',
    '<p>',
    `<b>Trade ID:</b> ${escapeHtml(p.subtradeIdLabel)}<br>`,
    `<b>Trade Date:</b> ${escapeHtml(p.tradeDate)}<br>`,
    `<b>Settlement Date:</b> ${escapeHtml(p.settlementDate)}<br>`,
    `<b>Deal Name:</b> ${escapeHtml(p.dealName)}<br>`,
    `<b>Seller:</b> ${escapeHtml(p.sellerName)}<br>`,
    `<b>Buyer:</b> ${escapeHtml(p.buyerName)}<br>`,
    `<b>Payment Method:</b> ${escapeHtml(p.paymentMethod)}<br>`,
    `<b>Trade PDF Contents:</b> ${escapeHtml(p.pdfContents)}`,
    '</p>',
    `<p>Please email <a href="mailto:${escapeHtml(p.operationsEmail)}">${escapeHtml(p.operationsEmail)}</a>`,
    ' if you have any questions regarding the above information.</p>',
    '<p style="font-size:11px;color:#888;"><b>Confidentiality Notice:</b> This email may contain privileged, confidential, and/or sensitive information intended solely for the use of the designated recipient. If you are not the intended recipient, you are hereby notified that any disclosure, copying, distribution, or use of the contents of this transmission is strictly prohibited. If you have received this communication in error, please notify the sender immediately and permanently delete all copies, whether electronic or physical.</p>',
    '<p style="font-size:11px;color:#888;">This communication may include nonpublic personal information (NPI) subject to the privacy and data protection requirements of the Gramm-Leach-Bliley Act (GLBA) and the Sarbanes-Oxley Act (SOX). Such information may not be reused or disclosed for any purpose other than to perform the services for which it was provided.</p>',
    '<p style="font-size:11px;color:#888;">Please note that electronic communications involve inherent risks. LendOS does not control the method of transmission or any third-party service providers used in connection with this message and assumes no liability for any delays, interceptions, or unauthorized access.</p>',
    '</body></html>',
  ].join('')

  const headers = [
    'MIME-Version: 1.0',
    'Date: ' + new Date().toUTCString(),
    `To: ${sanitizeHeaderValue(p.to.join('; '))}`,
    ...(p.cc?.length ? [`Cc: ${sanitizeHeaderValue(p.cc.join('; '))}`] : []),
    `Subject: ${subject}`,
    'X-Unsent: 1',
    `Content-Type: multipart/mixed; boundary="${boundary}"`,
  ].join('\r\n')

  const bodyPart = [
    `--${boundary}`,
    'Content-Type: text/html; charset=utf-8',
    'Content-Transfer-Encoding: base64',
    '',
    utf8ToBase64(html).replace(/(.{76})/g, '$1\r\n'),
  ].join('\r\n')

  const attachmentPart = [
    `--${boundary}`,
    `Content-Type: application/pdf; name="${p.pdfFileName}"`,
    'Content-Transfer-Encoding: base64',
    `Content-Disposition: attachment; filename="${p.pdfFileName}"`,
    '',
    p.pdfBase64.replace(/(.{76})/g, '$1\r\n'),
  ].join('\r\n')

  return [headers, '', bodyPart, attachmentPart, `--${boundary}--`, ''].join('\r\n')
}
